import axios from '../../../../redux/apiCaller';
import {
  successAction,
  attemptAction,
  errorAction,
} from '../../../../constants';
import {
  AILIST_ATTEMPT,
  AIACTION_ATTEMPT,
  AI_ERROR,
  FETCHALLAIRUNS_SUCCESS,
  FETCHAIRUN_ATTEMPT,
  FETCHAIRUN_SUCCESS,
  ADDAIRUN_SUCCESS,
  DELETEAIRUN_SUCCESS,
  ARCHIVEAIRUN_SUCCESS,
  RESTOREAIRUN_SUCCESS,
  FETCHAISETTINGS_SUCCESS,
  UPDATEAISETTINGS_SUCCESS,
  ADDAIMESSAGE_SUCCESS,
  FETCHAIASSETS_SUCCESS,
  ADDAIASSET_SUCCESS,
  DELETEAIASSET_SUCCESS,
  UPDATEAIASSET_SUCCESS,
  GENERATE_VIDEO_SUCCESS,
  FETCH_GIF_QUEUE_SUCCESS,
  ADD_GIF_QUEUE_SUCCESS,
  DELETE_GIF_QUEUE_SUCCESS,
  FETCHAILIBRARYASSETS_SUCCESS,
  SAVEAILIBRARYASSET_SUCCESS,
  DELETEAILIBRARYASSET_SUCCESS,
  UPDATE_RUN_DRAFT,
  REFRESHAIRUN_SUCCESS,
} from '../constants.js';
import { enqueueSnackbar } from 'redux/actions';

export const updateRunDraft = (runId, prompt) => ({
  type: UPDATE_RUN_DRAFT,
  payload: { runId, prompt },
});

const aiUrl = 'admin/ai';

export const fetchAiSettings = () => {
  const thunk = async function thunk(dispatch) {
    try {
      dispatch(attemptAction(AIACTION_ATTEMPT));
      const body = await axios.get(`${aiUrl}/settings`);
      dispatch(successAction(FETCHAISETTINGS_SUCCESS, body.data));
    } catch (err) {
      dispatch(errorAction(AI_ERROR, err));
    }
  };
  return thunk;
};

export const updateAiSettings = (data) => {
  const thunk = async function thunk(dispatch) {
    try {
      dispatch(attemptAction(AIACTION_ATTEMPT));
      const body = await axios.put(`${aiUrl}/settings`, data);
      dispatch(successAction(UPDATEAISETTINGS_SUCCESS, body.data));
      dispatch(
        enqueueSnackbar({
          message: 'AI settings saved',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'success',
          },
        })
      );
    } catch (err) {
      dispatch(
        enqueueSnackbar({
          message: err.data?.message || 'Failed to save AI settings',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'error',
          },
        })
      );
      dispatch(errorAction(AI_ERROR, err));
    }
  };
  return thunk;
};

export const resetAiSystemPromptToDefault = () => {
  const thunk = async function thunk(dispatch) {
    try {
      dispatch(attemptAction(AIACTION_ATTEMPT));
      const body = await axios.put(`${aiUrl}/settings/system-prompt/default`);
      dispatch(successAction(UPDATEAISETTINGS_SUCCESS, body.data));
      dispatch(
        enqueueSnackbar({
          message: 'System prompt reset to default',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'success',
          },
        })
      );
    } catch (err) {
      dispatch(
        enqueueSnackbar({
          message: err.data?.message || 'Failed to reset system prompt',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'error',
          },
        })
      );
      dispatch(errorAction(AI_ERROR, err));
    }
  };
  return thunk;
};

export const fetchAllAiRuns = (params = {}) => {
  const thunk = async function thunk(dispatch) {
    try {
      dispatch(attemptAction(AILIST_ATTEMPT));
      const body = await axios.get(`${aiUrl}/runs`, { params });
      dispatch(successAction(FETCHALLAIRUNS_SUCCESS, body.data));
    } catch (err) {
      dispatch(errorAction(AI_ERROR, err));
    }
  };
  return thunk;
};

export const fetchAiRun = (id) => {
  const thunk = async function thunk(dispatch) {
    try {
      dispatch({ type: FETCHAIRUN_ATTEMPT, payload: { id } });
      const body = await axios.get(`${aiUrl}/runs/${id}`);
      dispatch(successAction(FETCHAIRUN_SUCCESS, { ...body.data, requestedRunId: id }));
    } catch (err) {
      dispatch({
        ...errorAction(AI_ERROR, err),
        meta: { requestedRunId: id },
      });
    }
  };
  return thunk;
};

/**
 * Silently re-fetch the run without clearing existing state (no flicker).
 * Used after image/video generation to guarantee fresh signed URLs.
 */
export const refreshAiRun = (id) => {
  const thunk = async function thunk(dispatch) {
    try {
      const body = await axios.get(`${aiUrl}/runs/${id}`);
      dispatch(successAction(REFRESHAIRUN_SUCCESS, body.data));
    } catch (err) {
      // Silent refresh — don't show errors or clear state
    }
  };
  return thunk;
};

export const updateAiRunSettings = (id, data) => {
  const thunk = async function thunk(dispatch) {
    try {
      dispatch(attemptAction(AIACTION_ATTEMPT));
      const body = await axios.patch(`${aiUrl}/runs/${id}/settings`, data);
      dispatch(successAction(FETCHAIRUN_SUCCESS, body.data));
      dispatch(
        enqueueSnackbar({
          message: 'Run settings updated',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'success',
          },
        })
      );
      return body.data.run;
    } catch (err) {
      dispatch(
        enqueueSnackbar({
          message: err.data?.message || 'Failed to update run settings',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'error',
          },
        })
      );
      dispatch(errorAction(AI_ERROR, err));
      return null;
    }
  };
  return thunk;
};
export const createAiRun = () => {
  const thunk = async function thunk(dispatch) {
    try {
      dispatch(attemptAction(AIACTION_ATTEMPT));
      const body = await axios.post(`${aiUrl}/runs`, {});
      dispatch(successAction(ADDAIRUN_SUCCESS, body.data));
      return body.data.run;
    } catch (err) {
      dispatch(
        enqueueSnackbar({
          message: err.data?.message || 'Failed to create AI run',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'error',
          },
        })
      );
      dispatch(errorAction(AI_ERROR, err));
      return null;
    }
  };
  return thunk;
};

export const sendAiMessage = (id, prompt, files = [], assetIds = []) => {
  const thunk = async function thunk(dispatch) {
    try {
      dispatch(attemptAction(AIACTION_ATTEMPT));
      const body = await axios.post(`${aiUrl}/runs/${id}/messages`, { prompt, assetIds });
      dispatch(successAction(ADDAIMESSAGE_SUCCESS, body.data));
      return body.data.run;
    } catch (err) {
      dispatch(
        enqueueSnackbar({
          message: err.data?.message || 'Failed to generate image',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'error',
          },
        })
      );
      dispatch(errorAction(AI_ERROR, err));
      return null;
    }
  };
  return thunk;
};

export const getAiImageUrl = async (runId, messageIndex, download = false) => {
  const body = await axios.get(
    `${aiUrl}/runs/${runId}/images/${messageIndex}`,
    { params: { download: download ? 'true' : 'false' } }
  );
  return body.data.url;
};

export const upscaleAiImage = async (runId, messageIndex) => {
  const body = await axios.post(`${aiUrl}/runs/${runId}/images/${messageIndex}/upscale`);
  return body.data;
};

export const checkUpscaleStatus = async (runId, messageIndex) => {
  const body = await axios.get(`${aiUrl}/runs/${runId}/images/${messageIndex}/upscale`);
  return body.data;
};

export const upscaleAiLibraryAsset = async (assetId) => {
  const body = await axios.post(`${aiUrl}/library/${assetId}/upscale`);
  return body.data;
};

export const archiveAiRun = (id) => {
  const thunk = async function thunk(dispatch) {
    try {
      dispatch(attemptAction(AIACTION_ATTEMPT));
      await axios.put(`${aiUrl}/runs/${id}/archive`);
      dispatch(successAction(ARCHIVEAIRUN_SUCCESS, id));
      dispatch(
        enqueueSnackbar({
          message: 'Run archived',
          options: { key: new Date().getTime() + Math.random(), variant: 'success' },
        })
      );
    } catch (err) {
      dispatch(
        enqueueSnackbar({
          message: err.data?.message || 'Failed to archive run',
          options: { key: new Date().getTime() + Math.random(), variant: 'error' },
        })
      );
      dispatch(errorAction(AI_ERROR, err));
    }
  };
  return thunk;
};

export const restoreAiRun = (id) => {
  const thunk = async function thunk(dispatch) {
    try {
      dispatch(attemptAction(AIACTION_ATTEMPT));
      await axios.put(`${aiUrl}/runs/${id}/restore`);
      dispatch(successAction(RESTOREAIRUN_SUCCESS, id));
      dispatch(
        enqueueSnackbar({
          message: 'Run restored',
          options: { key: new Date().getTime() + Math.random(), variant: 'success' },
        })
      );
    } catch (err) {
      dispatch(
        enqueueSnackbar({
          message: err.data?.message || 'Failed to restore run',
          options: { key: new Date().getTime() + Math.random(), variant: 'error' },
        })
      );
      dispatch(errorAction(AI_ERROR, err));
    }
  };
  return thunk;
};

export const deleteAiRun = (id) => {
  const thunk = async function thunk(dispatch) {
    try {
      dispatch(attemptAction(AIACTION_ATTEMPT));
      await axios.delete(`${aiUrl}/runs/${id}`);
      dispatch(successAction(DELETEAIRUN_SUCCESS, id));
      dispatch(
        enqueueSnackbar({
          message: 'Run deleted',
          options: { key: new Date().getTime() + Math.random(), variant: 'success' },
        })
      );
    } catch (err) {
      dispatch(
        enqueueSnackbar({
          message: err.data?.message || 'Failed to delete run',
          options: { key: new Date().getTime() + Math.random(), variant: 'error' },
        })
      );
      dispatch(errorAction(AI_ERROR, err));
    }
  };
  return thunk;
};

export const fetchAiAssets = (page = 1, limit = 25, search = '', orderBy = 'createdAt', orderDir = 'desc') => {
  const thunk = async function thunk(dispatch) {
    try {
      dispatch(attemptAction(AILIST_ATTEMPT));
      const body = await axios.get(`${aiUrl}/assets`, {
        params: { page, limit, search, orderBy, orderDir },
      });
      dispatch(successAction(FETCHAIASSETS_SUCCESS, body.data));
      return body.data.docs || [];
    } catch (err) {
      dispatch(errorAction(AI_ERROR, err));
      return [];
    }
  };
  return thunk;
};

export const createAiAsset = (title, urlOrKey, file) => {
  const thunk = async function thunk(dispatch) {
    try {
      dispatch(attemptAction(AIACTION_ATTEMPT));
      let body;
      if (file) {
        const formData = new FormData();
        formData.append('title', title);
        formData.append('image', file);
        if (urlOrKey) formData.append('urlOrKey', urlOrKey);
        body = await axios.post(`${aiUrl}/assets`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        body = await axios.post(`${aiUrl}/assets`, { title, urlOrKey });
      }
      dispatch(successAction(ADDAIASSET_SUCCESS, body.data));
      dispatch(
        enqueueSnackbar({
          message: 'Asset created successfully',
          options: { key: new Date().getTime() + Math.random(), variant: 'success' },
        })
      );
      return true;
    } catch (err) {
      dispatch(
        enqueueSnackbar({
          message: err.data?.message || 'Failed to create asset',
          options: { key: new Date().getTime() + Math.random(), variant: 'error' },
        })
      );
      dispatch(errorAction(AI_ERROR, err));
      return false;
    }
  };
  return thunk;
};

export const updateAiAsset = (id, title, urlOrKey, file) => {
  const thunk = async function thunk(dispatch) {
    try {
      dispatch(attemptAction(AIACTION_ATTEMPT));
      let body;
      if (file) {
        const formData = new FormData();
        if (title) formData.append('title', title);
        formData.append('image', file);
        if (urlOrKey) formData.append('urlOrKey', urlOrKey);
        body = await axios.put(`${aiUrl}/assets/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        body = await axios.put(`${aiUrl}/assets/${id}`, { title, urlOrKey });
      }
      dispatch(successAction(UPDATEAIASSET_SUCCESS, body.data));
      dispatch(
        enqueueSnackbar({
          message: 'Asset updated successfully',
          options: { key: new Date().getTime() + Math.random(), variant: 'success' },
        })
      );
      return true;
    } catch (err) {
      dispatch(
        enqueueSnackbar({
          message: err.data?.message || 'Failed to update asset',
          options: { key: new Date().getTime() + Math.random(), variant: 'error' },
        })
      );
      dispatch(errorAction(AI_ERROR, err));
      return false;
    }
  };
  return thunk;
};

export const deleteAiAsset = (id) => {
  const thunk = async function thunk(dispatch) {
    try {
      dispatch(attemptAction(AIACTION_ATTEMPT));
      const body = await axios.delete(`${aiUrl}/assets/${id}`);
      dispatch(successAction(DELETEAIASSET_SUCCESS, body.data));
      dispatch(
        enqueueSnackbar({
          message: 'Asset deleted successfully',
          options: { key: new Date().getTime() + Math.random(), variant: 'success' },
        })
      );
      return true;
    } catch (err) {
      dispatch(
        enqueueSnackbar({
          message: err.data?.message || 'Failed to delete asset',
          options: { key: new Date().getTime() + Math.random(), variant: 'error' },
        })
      );
      dispatch(errorAction(AI_ERROR, err));
      return false;
    }
  };
  return thunk;
};

export const resolveAssetTitles = async (titles) => {
  try {
    const body = await axios.post(`${aiUrl}/assets/resolve`, { titles });
    return body.data?.assets || [];
  } catch (err) {
    return [];
  }
};

export const generateVideo = (runId, prompt, assetIds = []) => {
  const thunk = async function thunk(dispatch) {
    try {
      dispatch(attemptAction(AIACTION_ATTEMPT));
      const body = await axios.post(`${aiUrl}/runs/${runId}/video`, { prompt, assetIds });
      dispatch(successAction(GENERATE_VIDEO_SUCCESS, body.data));
      dispatch(
        enqueueSnackbar({
          message: 'Video generation in progress',
          options: { key: new Date().getTime() + Math.random(), variant: 'success' },
        })
      );
      return body.data.run;
    } catch (err) {
      dispatch(
        enqueueSnackbar({
          message: err.data?.message || 'Failed to generate video',
          options: { key: new Date().getTime() + Math.random(), variant: 'error' },
        })
      );
      dispatch(errorAction(AI_ERROR, err));
      return null;
    }
  };
  return thunk;
};

export const fetchGifQueue = (page = 1, limit = 25, isPolling = false, orderBy = 'createdAt', orderDir = 'desc') => {
  const thunk = async function thunk(dispatch) {
    try {
      if (!isPolling) dispatch(attemptAction(AILIST_ATTEMPT));
      const body = await axios.get(`${aiUrl}/gif-queue`, { params: { page, limit, orderBy, orderDir } });
      dispatch(successAction(FETCH_GIF_QUEUE_SUCCESS, body.data));
      return body.data.data;
    } catch (err) {
      if (!isPolling) dispatch(errorAction(AI_ERROR, err));
      return { docs: [], totalDocs: 0, page: 1, totalPages: 1 };
    }
  };
  return thunk;
};

export const addToGifQueue = (videoUrls, runId, messageIndex, trimDuration) => {
  const thunk = async function thunk(dispatch) {
    try {
      const body = await axios.post(`${aiUrl}/gif-queue`, { videoUrls, runId, messageIndex, trimDuration });
      dispatch(successAction(ADD_GIF_QUEUE_SUCCESS, body.data));
      dispatch(
        enqueueSnackbar({
          message: 'Added to GIF generation queue',
          options: { key: new Date().getTime() + Math.random(), variant: 'success' },
        })
      );
      return true;
    } catch (err) {
      dispatch(
        enqueueSnackbar({
          message: err.data?.message || 'Failed to add to queue',
          options: { key: new Date().getTime() + Math.random(), variant: 'error' },
        })
      );
      return false;
    }
  };
  return thunk;
};

export const processGifQueue = (jobIds = []) => {
  const thunk = async function thunk(dispatch) {
    try {
      dispatch(attemptAction(AIACTION_ATTEMPT));
      await axios.post(`${aiUrl}/gif-queue/process`, { jobIds });
      dispatch(
        enqueueSnackbar({
          message: 'Started processing GIF queue',
          options: { key: new Date().getTime() + Math.random(), variant: 'success' },
        })
      );
      // We don't have a specific success action for this, we'll just refetch in the component
      return true;
    } catch (err) {
      dispatch(
        enqueueSnackbar({
          message: err.data?.message || 'Failed to process queue',
          options: { key: new Date().getTime() + Math.random(), variant: 'error' },
        })
      );
      dispatch(errorAction(AI_ERROR, err));
      return false;
    }
  };
  return thunk;
};

export const deleteGifJob = (jobId) => {
  const thunk = async function thunk(dispatch) {
    try {
      dispatch(attemptAction(AIACTION_ATTEMPT));
      await axios.delete(`${aiUrl}/gif-queue/${jobId}`);
      dispatch(successAction(DELETE_GIF_QUEUE_SUCCESS, jobId));
      dispatch(
        enqueueSnackbar({
          message: 'Removed from queue',
          options: { key: new Date().getTime() + Math.random(), variant: 'success' },
        })
      );
      return true;
    } catch (err) {
      dispatch(
        enqueueSnackbar({
          message: err.data?.message || 'Failed to remove from queue',
          options: { key: new Date().getTime() + Math.random(), variant: 'error' },
        })
      );
      dispatch(errorAction(AI_ERROR, err));
      return false;
    }
  };
  return thunk;
};

export const fetchAiLibraryAssets = (page = 1, limit = 25, search = '', orderBy = 'createdAt', orderDir = 'desc') => {
  const thunk = async function thunk(dispatch) {
    try {
      dispatch(attemptAction(AILIST_ATTEMPT));
      const body = await axios.get(`${aiUrl}/library`, {
        params: { page, limit, search, orderBy, orderDir },
      });
      dispatch(successAction(FETCHAILIBRARYASSETS_SUCCESS, body.data));
      return body.data.assets || [];
    } catch (err) {
      dispatch(errorAction(AI_ERROR, err));
      return [];
    }
  };
  return thunk;
};

export const saveAiLibraryAsset = (runId, messageIndex, title) => {
  const thunk = async function thunk(dispatch) {
    try {
      dispatch(attemptAction(AIACTION_ATTEMPT));
      const body = await axios.post(`${aiUrl}/library/save`, { runId, messageIndex, title });
      dispatch(successAction(SAVEAILIBRARYASSET_SUCCESS, body.data));
      dispatch(
        enqueueSnackbar({
          message: 'Image saved to library',
          options: { key: new Date().getTime() + Math.random(), variant: 'success' },
        })
      );
      return true;
    } catch (err) {
      dispatch(
        enqueueSnackbar({
          message: err.data?.message || 'Failed to save image',
          options: { key: new Date().getTime() + Math.random(), variant: 'error' },
        })
      );
      dispatch(errorAction(AI_ERROR, err));
      return false;
    }
  };
  return thunk;
};

export const deleteAiLibraryAsset = (id) => {
  const thunk = async function thunk(dispatch) {
    try {
      dispatch(attemptAction(AIACTION_ATTEMPT));
      await axios.delete(`${aiUrl}/library/${id}`);
      dispatch(successAction(DELETEAILIBRARYASSET_SUCCESS, id));
      dispatch(
        enqueueSnackbar({
          message: 'Image removed from library',
          options: { key: new Date().getTime() + Math.random(), variant: 'success' },
        })
      );
      return true;
    } catch (err) {
      dispatch(
        enqueueSnackbar({
          message: err.data?.message || 'Failed to remove image',
          options: { key: new Date().getTime() + Math.random(), variant: 'error' },
        })
      );
      dispatch(errorAction(AI_ERROR, err));
      return false;
    }
  };
  return thunk;
};

export const getLibraryAssetDownloadUrl = async (id) => {
  try {
    const body = await axios.get(`${aiUrl}/library/${id}/download`);
    return body.data?.url || null;
  } catch (err) {
    return null;
  }
};
